import { clearAuthToken } from '@/lib/auth-token';
import {
  getDeletionAuthProof,
  saveDeletionAuthProof,
} from '@/lib/deletion-auth-proof';
import { apiClient } from '@borradh-workspace/api-client';
import {
  deletedItemRestoredSchema,
  organizationDeletionRequestedSchema,
  requestOrganizationDeletionRequestSchema,
  restoreOrganizationDeletionRequestSchema,
} from '@borradh-workspace/contracts';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
export const DELETION_INTENT_KEY = 'borrah-workspace-deletion-intent';
export function useRequestWorkspaceDeletion(input: {
  organizationId?: string;
  expectedName: string;
  validIntent: () => boolean;
  onError: (error: Error) => void;
}) {
  return useMutation({
    mutationFn: async () => {
      if (!input.validIntent())
        throw new Error('Sign in again to confirm deletion of this clinic.');
      return apiClient.post(
        `organizations/${input.organizationId}/deletion`,
        requestOrganizationDeletionRequestSchema.parse({
          confirm: 'DELETE_ORGANIZATION',
          expectedName: input.expectedName,
        }),
        {
          schema: organizationDeletionRequestedSchema,
          headers: { 'X-Deletion-Auth-Proof': getDeletionAuthProof() ?? '' },
        }
      );
    },
    onSuccess: () => {
      sessionStorage.removeItem(DELETION_INTENT_KEY);
      saveDeletionAuthProof(undefined);
    },
    onError: input.onError,
  });
}
/** Runs after the confirmation screen has played. The workspace is already
 * closed server-side, so it stays hidden even if sign-out fails. */
export async function signOutAfterWorkspaceDeletion() {
  await apiClient.post('auth/sign-out', undefined).catch(() => {});
  clearAuthToken();
  window.location.assign('/sign-in');
}
export function useRestoreWorkspace() {
  return useMutation({
    mutationFn: () =>
      apiClient.post(
        'organizations/pending-deletion/restore',
        restoreOrganizationDeletionRequestSchema.parse({
          confirm: 'RESTORE_ORGANIZATION',
        }),
        { schema: deletedItemRestoredSchema }
      ),
    onSuccess: () => window.location.assign('/dashboard'),
    onError: (error: Error) =>
      toast.error(error.message || 'Unable to restore organization'),
  });
}
